import { ButtonGradientPrimary, Colors, FOCUS_RING, Icon, Text } from '@amelio/ui-library';

import { useAgendaEditorContext } from '../AgendaEditorContext';
import { AGENDA_RESPONSE_MAX_LENGTH } from '../agendaLimits';

import type { AgendaPointView } from '../agendaViewModel';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { GripVerticalIcon } from '@/components/GripVerticalIcon/GripVerticalIcon';
import { useAmelioTranslations } from '@/hooks';

/**
 * The square control the row's four icon buttons wear: same target, same radius, same disabled
 * reading, so confirming, cancelling, editing and deleting a point cannot drift apart. Only the
 * tone is the caller's, since it is what tells them apart.
 */
const ICON_BUTTON =
    'focus-visible:ring-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40';

/** Raised above the row, for the two controls that sit on the row's own surface rather than in a bar. */
const ICON_BUTTON_RAISED = `${ICON_BUTTON} shadow`;

const ICON_BUTTON_PRIMARY = 'bg-primary-500 hover:bg-primary-600';
const ICON_BUTTON_MUTED = 'bg-base-50 hover:bg-base-100';
const ICON_BUTTON_DESTRUCTIVE = 'bg-error hover:bg-error-800';

interface AgendaDiscussionPointRowProps {
    itemId: string;
    point: AgendaPointView;
    /** The response's accordion, held by the topic so a single response is open at a time. */
    isResponseExpanded: boolean;
    onToggleResponse: () => void;
    isDragging?: boolean;
    dragListeners?: SyntheticListenerMap;
    dragAttributes?: DraggableAttributes;
}

interface PointTitleProps {
    itemId: string;
    point: AgendaPointView;
}

/** The point's title: typed into while it is renamed, read as text on a record, else the way into its details. */
const PointTitle = ({ itemId, point }: PointTitleProps) => {
    const {
        editor: { points },
        disabled,
        isRecord,
    } = useAgendaEditorContext();

    if (points.titleEdit?.pointId === point.id) {
        return (
            <input
                type={'text'}
                value={points.titleEdit.title}
                onChange={(event) => points.changeTitle(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.stopPropagation();
                        points.confirmTitle();
                    }
                }}
                disabled={disabled}
                autoFocus
                // Same size, weight, line-height and color as the read-mode `Text`
                // below, so the row doesn't change height when a title enters or
                // leaves editing.
                className={
                    'focus-visible:ring-primary border-primary-200 bg-primary-50/40 disabled:bg-base-50 w-full rounded-lg border p-3 text-base leading-6 font-semibold focus:outline-none focus-visible:ring-1'
                }
                style={{ color: Colors.BASE }}
                data-testid={`edit-discussion-point-title-input-${point.id}`}
            />
        );
    }

    if (isRecord) {
        return (
            <Text
                size={'default'}
                weight={'semibold'}
                className={'leading-6 wrap-break-word whitespace-normal'}
            >
                {point.title}
            </Text>
        );
    }

    return (
        <button
            type={'button'}
            onClick={() => points.openDetails({ itemId, point })}
            disabled={disabled}
            className={
                'focus-visible:ring-primary hover:text-primary flex min-h-[24px] min-w-0 cursor-pointer items-center rounded-lg border-0 bg-transparent p-0 text-left text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-default'
            }
            data-testid={`open-discussion-point-details-button-${point.id}`}
        >
            <Text
                size={'default'}
                weight={'semibold'}
                color={'inherit'}
                className={'leading-6 tracking-[0.16px] wrap-break-word whitespace-normal'}
            >
                {point.title}
            </Text>
        </button>
    );
};

interface FoldedResponseProps {
    pointId: string;
    response: string;
    onOpen: () => void;
}

/** A noted response, folded away: it still reads under its label, and opens where it is edited. */
const FoldedResponse = ({ pointId, response, onOpen }: FoldedResponseProps) => {
    const { t } = useAmelioTranslations();

    return (
        <button
            type={'button'}
            onClick={onOpen}
            className={`hover:bg-base-50 w-full cursor-pointer px-4 py-2 text-left ${FOCUS_RING}`}
            data-testid={`discussion-point-response-preview-${pointId}`}
        >
            {/* Named by the response it shows, with the action said first to a screen reader. */}
            <span className={'sr-only'}>{t('meetingAgenda.editEmployeeResponse')}</span>
            <Text
                size={'small'}
                color={Colors.BASE_500}
                className={'line-clamp-3 whitespace-pre-line italic'}
            >
                {response}
            </Text>
        </button>
    );
};

export const AgendaDiscussionPointRow = ({
    itemId,
    point,
    isResponseExpanded,
    onToggleResponse,
    isDragging = false,
    dragListeners,
    dragAttributes,
}: AgendaDiscussionPointRowProps) => {
    const { t } = useAmelioTranslations();
    const {
        editor: { points },
        disabled,
        isRecord,
        withResponses,
    } = useAgendaEditorContext();
    const isTitleEditing = points.titleEdit?.pointId === point.id;

    return (
        // Two columns: the grip shares the first row with the question box, so it centers on the box
        // alone; the response sits in the second row, under the box. Without a grip the first column
        // is empty and takes no room.
        <div
            className={`grid-cols-handle grid items-center ${disabled ? '' : 'gap-x-3'} ${isDragging ? 'bg-primary-50 rounded-lg' : ''}`}
        >
            {/* No grip on a read-only agenda: the list refuses the reorder, as for its topics. */}
            {!disabled && (
                <span
                    className={`text-base-300 hover:text-primary cursor-move ${FOCUS_RING}`}
                    data-testid={`discussion-point-drag-handle-${point.id}`}
                    {...dragListeners}
                    {...dragAttributes}
                    aria-label={t('meetingAgenda.reorderDiscussionPoint')}
                >
                    <GripVerticalIcon className={'h-[24px] w-[24px]'} />
                </span>
            )}

            <div
                className={
                    'group border-base-100 hover:border-base-200 col-start-2 flex w-full items-center gap-3 rounded-lg border bg-white px-5 py-4 transition-colors hover:bg-white/80'
                }
                data-testid={`discussion-point-${point.id}`}
            >
                <div className={'flex min-w-0 flex-1 flex-col gap-1'}>
                    <PointTitle
                        itemId={itemId}
                        point={point}
                    />

                    {/* On a record the context is read where the point is, not announced as a
                        detail behind a control that no longer opens. */}
                    {isRecord && point.context && (
                        <Text
                            size={'small'}
                            color={Colors.BASE_500}
                            className={'whitespace-pre-line'}
                        >
                            {point.context}
                        </Text>
                    )}

                    {point.hasDetails && !isRecord && (
                        <div className={'flex items-center gap-1'}>
                            <Icon
                                name={'essential-tick-circle'}
                                size={'xs'}
                                color={Colors.PRIMARY}
                            />

                            <Text
                                size={'small'}
                                weight={'medium'}
                                color={Colors.PRIMARY}
                                className={'text-[11px]'}
                            >
                                {t('meetingAgenda.detailsAdded')}
                            </Text>
                        </div>
                    )}
                </div>

                {isTitleEditing && (
                    <div className={'flex shrink-0 items-center gap-1'}>
                        <button
                            type={'button'}
                            onClick={points.confirmTitle}
                            disabled={disabled}
                            className={`${ICON_BUTTON} ${ICON_BUTTON_PRIMARY}`}
                            aria-label={t('meetingAgenda.saveChanges')}
                            data-testid={`save-discussion-point-title-button-${point.id}`}
                        >
                            <Icon
                                name={'essential-checkmark'}
                                size={'sm'}
                                color={Colors.WHITE}
                            />
                        </button>

                        <button
                            type={'button'}
                            onClick={points.cancelTitle}
                            disabled={disabled}
                            className={`${ICON_BUTTON} ${ICON_BUTTON_MUTED}`}
                            aria-label={t('common.cancel')}
                            data-testid={`cancel-discussion-point-title-button-${point.id}`}
                        >
                            <Icon
                                name={'essential-close'}
                                size={'sm'}
                                color={Colors.BASE_400}
                            />
                        </button>
                    </div>
                )}

                {!isTitleEditing && !isRecord && (
                    <div
                        className={
                            'flex shrink-0 items-center gap-1 group-hover:opacity-100 md:opacity-0'
                        }
                    >
                        <button
                            type={'button'}
                            onClick={() => points.startTitleEdit({ itemId, point })}
                            disabled={disabled}
                            className={`${ICON_BUTTON_RAISED} ${ICON_BUTTON_PRIMARY}`}
                            aria-label={t('meetingAgenda.editDiscussionPoint')}
                            data-testid={`edit-discussion-point-title-button-${point.id}`}
                        >
                            <Icon
                                name={'edit-edit-2'}
                                size={'sm'}
                                color={Colors.WHITE}
                            />
                        </button>

                        <button
                            type={'button'}
                            onClick={() => points.remove({ itemId, pointId: point.id })}
                            disabled={disabled}
                            className={`${ICON_BUTTON_RAISED} ${ICON_BUTTON_DESTRUCTIVE}`}
                            aria-label={t('meetingAgenda.deleteDiscussionPoint')}
                            data-testid={`delete-discussion-point-button-${point.id}`}
                        >
                            <Icon
                                name={'essential-close'}
                                size={'sm'}
                                color={Colors.WHITE}
                            />
                        </button>
                    </div>
                )}
            </div>

            {withResponses && (
                <div className={'col-start-2'}>
                    <div className={'flex gap-2'}>
                        <button
                            type={'button'}
                            onClick={onToggleResponse}
                            // `min-h-8` matches the edit/delete controls' 32px target above, so the
                            // three controls on this discussion point share the same minimum size.
                            className={
                                'hover:bg-base-50 mt-3 flex min-h-8 w-full items-center gap-2.5 rounded-lg px-4 py-2'
                            }
                            data-testid={`toggle-discussion-point-response-${point.id}`}
                        >
                            <Icon
                                name={'arrow-arrow-down'}
                                rotate={isResponseExpanded ? 180 : 270}
                                size={'sm'}
                                color={Colors.BASE_400}
                            />

                            <Text
                                weight={'medium'}
                                color={Colors.BASE_400}
                                className={'leading-6'}
                            >
                                {t('meetingAgenda.employeeResponseLabel')}
                            </Text>
                        </button>
                    </div>

                    {!isResponseExpanded && !isRecord && point.response?.trim() && (
                        <FoldedResponse
                            pointId={point.id}
                            response={point.response}
                            onOpen={onToggleResponse}
                        />
                    )}

                    {isResponseExpanded && isRecord && (
                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                            className={'mt-2 px-4 whitespace-pre-line'}
                        >
                            {point.response || t('meetingAgenda.readOnly.noResponse')}
                        </Text>
                    )}

                    {isResponseExpanded && !isRecord && (
                        <div className={'mt-2 px-2'}>
                            <textarea
                                value={point.response ?? ''}
                                onChange={(event) =>
                                    points.changeResponse({
                                        itemId,
                                        pointId: point.id,
                                        response: event.target.value,
                                    })
                                }
                                onBlur={points.saveResponses}
                                disabled={disabled}
                                // Opened on purpose, from the chevron or from the folded response.
                                autoFocus
                                maxLength={AGENDA_RESPONSE_MAX_LENGTH}
                                placeholder={t('meetingAgenda.employeeResponsePlaceholder')}
                                className={
                                    'border-base-100 text-base-500 focus:border-primary disabled:bg-base-100 w-full rounded-lg border px-3 py-2 text-base focus:outline-2 focus:outline-offset-2 focus:outline-transparent'
                                }
                                rows={3}
                                data-testid={`discussion-point-response-${point.id}`}
                            />

                            <div className={'mt-2 flex justify-end'}>
                                <ButtonGradientPrimary
                                    size={'md'}
                                    fitContent
                                    disabled={disabled}
                                    leftIcon={
                                        <Icon
                                            name={'essential-checkmark'}
                                            size={'lg'}
                                            color={Colors.WHITE}
                                        />
                                    }
                                    onClick={() => {
                                        points.saveResponses();
                                        onToggleResponse();
                                    }}
                                    dataTestId={`discussion-point-response-done-${point.id}`}
                                    className={'!h-auto !rounded-full px-6 py-3 hover:scale-105'}
                                >
                                    {t('meetingAgenda.done')}
                                </ButtonGradientPrimary>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AgendaDiscussionPointRow;
